/**
 * AdminBiblePlans — super-admin curation of Bible reading plans.
 * CRUD via /api/superadmin/bible-plans.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Loader2, Plus, BookOpen, Star, Trash2, Edit, Sprout, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import BiblePlanDayBuilder, { cleanForApi } from '@/components/bible/BiblePlanDayBuilder';
import { MediaUpload } from '@/components/MediaUpload';
import PlanAssignDialog from '@/components/bible/PlanAssignDialog';
import PlanAssignmentsTable, { usePlanAssignments } from '@/components/bible/PlanAssignmentsTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const emptyForm = {
  title: '', description: '', cover_image_url: '', duration_days: 30,
  days: [], tags: [], featured: false, is_public: true,
};

const AdminBiblePlans = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // 'new' | plan object
  const [form, setForm] = useState(emptyForm);
  const [tagsText, setTagsText] = useState('');
  const [assignPlan, setAssignPlan] = useState(null);
  const { data: assignData } = usePlanAssignments();
  const assignmentsByPlan = (assignData?.assignments || []).reduce((acc, a) => {
    (acc[a.plan_id] = acc[a.plan_id] || []).push(a);
    return acc;
  }, {});

  const { data, isLoading } = useQuery({
    queryKey: ['bible-plans-admin'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/bible-plans`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed to load plans');
      return r.json();
    },
  });
  const plans = data?.items || [];

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        tags: tagsText.split(',').map(s => s.trim()).filter(Boolean),
        days: cleanForApi(form.days || []),
      };
      const isNew = dialog === 'new';
      const url = isNew
        ? `${API_URL}/api/superadmin/bible-plans`
        : `${API_URL}/api/superadmin/bible-plans/${dialog.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers: hdrs(), body: JSON.stringify(payload) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Plan saved');
      qc.invalidateQueries({ queryKey: ['bible-plans-admin'] });
      setDialog(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id) => {
      const r = await fetch(`${API_URL}/api/superadmin/bible-plans/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error('Delete failed');
      return r.json();
    },
    onSuccess: () => {
      toast.success('Plan deleted');
      qc.invalidateQueries({ queryKey: ['bible-plans-admin'] });
    },
  });

  const seedDefaults = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/bible-plans/seed-defaults`, { method: 'POST', headers: authHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Seed failed (${r.status})`);
      }
      return r.json();
    },
    onSuccess: (res) => {
      if (res.skipped) {
        toast.warning('Starter plans already exist — nothing to seed.', {
          description: 'Use "New plan" to add more, or delete existing ones first.',
          duration: 6000,
        });
      } else {
        toast.success(`Seeded ${res.created} starter plan${res.created === 1 ? '' : 's'}`, { duration: 5000 });
      }
      qc.invalidateQueries({ queryKey: ['bible-plans-admin'] });
    },
    onError: (e) => toast.error(`Seed failed: ${e.message}`, { duration: 6000 }),
  });

  const openNew = () => {
    setForm({ ...emptyForm, days: [] });
    setTagsText('');
    setDialog('new');
  };
  const openEdit = (p) => {
    setForm({ ...emptyForm, ...p, days: Array.isArray(p.days) ? p.days : [] });
    setTagsText((p.tags || []).join(', '));
    setDialog(p);
  };

  return (
    <DashboardLayout title="Bible Reading Plans">
      <div className="space-y-5" data-testid="admin-bible-plans-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-indigo-600" /> Bible Reading Plans
              </h1>
              <p className="text-xs text-slate-500">Curate public plans users can subscribe to.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => seedDefaults.mutate()} disabled={seedDefaults.isPending} data-testid="seed-plans-btn">
              <Sprout className="h-4 w-4 mr-1" /> Seed starter plans
            </Button>
            <Button onClick={openNew} data-testid="new-plan-btn">
              <Plus className="h-4 w-4 mr-1" /> New plan
            </Button>
          </div>
        </div>

        <Tabs defaultValue="plans" className="space-y-4">
          <TabsList data-testid="bible-plans-tabs">
            <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments">
              Assignments{(assignData?.total || 0) > 0 ? ` (${assignData.total})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : plans.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-slate-500">
              No reading plans yet. Click <strong>Seed starter plans</strong> to get three curated plans (Through the Bible in a Year, 21-Day Devotional, Psalms in 30).
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map(p => (
              <Card key={p.id} data-testid={`bible-plan-card-${p.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {p.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                      {p.title}
                    </CardTitle>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setAssignPlan(p)} data-testid={`plan-assign-${p.id}`} title="Assign to churches, provinces or events">
                        <Share2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => { if (window.confirm(`Delete "${p.title}"?`)) del.mutate(p.id); }} data-testid={`plan-del-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-slate-600 space-y-2">
                  <p className="line-clamp-2">{p.description || 'No description'}</p>
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-[10px]">{p.duration_days} days</Badge>
                    <Badge variant="outline" className="text-[10px]">{(p.days || []).length} day entries</Badge>
                    {p.is_public ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Public</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Private</Badge>
                    )}
                  </div>
                  <div className="rounded-md bg-slate-50 px-2.5 py-2" data-testid={`plan-assignments-summary-${p.id}`}>
                    {(assignmentsByPlan[p.id] || []).length === 0 ? (
                      <button className="text-[11px] text-slate-500 underline" onClick={() => setAssignPlan(p)}
                              data-testid={`plan-assign-link-${p.id}`}>
                        Not assigned yet — assign to a church, province or event
                      </button>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-[11px] font-medium text-slate-600">
                          Assigned to {(assignmentsByPlan[p.id] || []).length} target
                          {(assignmentsByPlan[p.id] || []).length === 1 ? '' : 's'}
                        </p>
                        {(assignmentsByPlan[p.id] || []).slice(0, 3).map((a) => (
                          <p key={a.id} className="text-[11px] text-slate-500 truncate">
                            {a.target_name} · {a.started ? 'started' : 'starts'} {a.start_date}
                            {a.is_active ? '' : ' · off'}
                          </p>
                        ))}
                        {(assignmentsByPlan[p.id] || []).length > 3 && (
                          <p className="text-[11px] text-slate-400">
                            +{(assignmentsByPlan[p.id] || []).length - 3} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {(p.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.tags.map(t => (
                        <span key={t} className="text-[10px] text-slate-500">#{t}</span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          <TabsContent value="assignments" className="space-y-3">
            <p className="text-xs text-slate-500">
              Every plan given to a church, a campus, a whole province or an event. Switch one off
              to hide it again — nobody loses the reading they have already done.
            </p>
            <PlanAssignmentsTable />
          </TabsContent>
        </Tabs>

        <PlanAssignDialog plan={assignPlan} open={!!assignPlan}
                          onOpenChange={(o) => !o && setAssignPlan(null)} />

        {/* Dialog */}
        <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bible-plan-dialog">
            <DialogHeader>
              <DialogTitle>{dialog === 'new' ? 'New reading plan' : `Edit ${form.title}`}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="plan-form-title" />
              </div>
              <div><Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Duration (days)</Label>
                  <Input type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: Number(e.target.value) })} data-testid="plan-form-days" />
                </div>
                <div><MediaUpload value={form.cover_image_url || ''} onChange={(url) => setForm({ ...form, cover_image_url: url })} purpose="cover" label="Cover image" testId="plan-form-cover-upload" previewClassName="h-16" />
                </div>
              </div>
              <div><Label>Tags (comma-separated)</Label>
                <Input value={tagsText} onChange={e => setTagsText(e.target.value)} placeholder="devotional, prayer, short" />
              </div>
              <div className="border-t pt-3">
                <BiblePlanDayBuilder
                  value={form.days || []}
                  onChange={(days) => setForm({ ...form, days })}
                />
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.featured} onCheckedChange={v => setForm({ ...form, featured: v })} />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.is_public} onCheckedChange={v => setForm({ ...form, is_public: v })} />
                  Public (subscribers can browse)
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="plan-form-save">
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminBiblePlans;
